import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { DiscordMessagePreview } from '@/components/DiscordMessagePreview';
import { useConfigPage, BackLink } from './_shared';

/**
 * ModMail-Config — the mod-mail consumer reads either the moduleConfig
 * `modMail.channelId` or the scalar `mod_channel`. We write both for
 * legacy compatibility.
 */
export function ModMailConfig() {
  const { guildId, settingsQuery, saveMut, currentConfig } = useConfigPage('modMail');
  const [channelId, setChannelId] = useState<string | null>(null);

  useEffect(() => {
    if (settingsQuery.data) {
      setChannelId((currentConfig.channelId as string) ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-6 text-2xl font-semibold">Mod-Mail — Konfiguration</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mod-Mail-Channel</CardTitle>
          <CardDescription>
            User können den Bot per DM anschreiben — Nachrichten landen hier.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Mod-Mail-Channel</Label>
            <ChannelPicker
              guildId={guildId}
              value={channelId}
              onChange={setChannelId}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
            />
          </div>

          <div className="flex items-center justify-end pt-2">
            <Button
              disabled={saveMut.isPending || !channelId}
              onClick={() =>
                saveMut.mutate({
                  moduleConfig: { channelId },
                  scalars: { modChannelId: channelId },
                })
              }
            >
              {saveMut.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
          {saveMut.isSuccess && <p className="text-sm text-success">Gespeichert ✓</p>}
          {saveMut.error && (
            <p className="text-sm text-destructive">
              Fehler: {(saveMut.error as Error).message}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="mt-6">
        <DiscordMessagePreview
          hint="So sieht eine neue ModMail-Nachricht im gewählten Channel aus."
          embed={{
            title: 'Neue ModMail-Nachricht',
            description:
              '**User**\n{USER} (123456789012345678)\n\n**Nachricht**\nHallo, ich habe eine Frage zu…',
            footer: { text: 'Antwort: /modmail-send <user> <message>' },
            timestamp: true,
          }}
        />
      </div>
    </div>
  );
}
